import { useEffect, useState } from 'react';
import { startOfWeek, subWeeks } from 'date-fns';
import api from '../../../../services/api';
import { useUserProfile } from '../../../user/hooks/useUserProfile';
import type { InfoBoardStatsState, TrackedTimeStats, TrackedTimeStatsRequest } from './types';

const TRACKED_TIME_STATS_URL = '/umbraco/api/ClientsRelationsReport/GetTrackedTimeStats';

type CompletedRequestState = {
  requestKey: string;
  data: TrackedTimeStats | null;
  error: string | null;
};

const toIsoWithDateOffset = (date: Date): string => {
  const pad = (value: number) => String(Math.floor(Math.abs(value))).padStart(2, '0');
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absOffsetMinutes = Math.abs(offsetMinutes);
  const offsetHours = Math.floor(absOffsetMinutes / 60);
  const offsetMins = absOffsetMinutes % 60;

  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.` +
    `${String(date.getMilliseconds()).padStart(3, '0')}` +
    `${sign}${pad(offsetHours)}:${pad(offsetMins)}`
  );
};

export const createTrackedTimeStatsRequest = (
  relationId: number,
  clientId: number,
  now = new Date()
): TrackedTimeStatsRequest => {
  const currentWeekStart = startOfWeek(now, { weekStartsOn: 1 });
  const lastWeekStart = subWeeks(currentWeekStart, 1);

  return {
    relationId,
    clientId,
    currentWeekStart: toIsoWithDateOffset(currentWeekStart),
    lastWeekStart: toIsoWithDateOffset(lastWeekStart),
    currentDateTime: toIsoWithDateOffset(now),
  };
};

export const useInfoBoardStats = (relationId: number): InfoBoardStatsState => {
  const { userProfile, loading: userLoading, error: userError } = useUserProfile();
  const clientId = userProfile?.Id;
  const requestKey = clientId ? `${relationId}:${clientId}` : null;
  const [completedRequest, setCompletedRequest] = useState<CompletedRequestState | null>(null);

  useEffect(() => {
    if (userLoading || !requestKey || !clientId) return;

    const controller = new AbortController();
    let active = true;

    api
      .get<TrackedTimeStats>(TRACKED_TIME_STATS_URL, {
        params: createTrackedTimeStatsRequest(relationId, clientId),
        signal: controller.signal,
      })
      .then((response) => {
        if (active) {
          setCompletedRequest({ requestKey, data: response.data, error: null });
        }
      })
      .catch(() => {
        if (active && !controller.signal.aborted) {
          setCompletedRequest({
            requestKey,
            data: null,
            error: 'Statistics are unavailable.',
          });
        }
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [clientId, relationId, requestKey, userLoading]);

  if (userLoading) {
    return { data: null, loading: true, error: null };
  }
  if (!clientId || !requestKey) {
    return {
      data: null,
      loading: false,
      error: userError || 'Statistics are unavailable.',
    };
  }
  if (completedRequest?.requestKey !== requestKey) {
    return { data: null, loading: true, error: null };
  }

  return {
    data: completedRequest.data,
    loading: false,
    error: completedRequest.error,
  };
};
